import { Loader } from "apps/visits/packages/ui/src/components/loader";
import { useTranslation } from "react-i18next";
import { Footer } from "@/core/shared/v2/footer";
import { Header } from "@/core/shared/v2/header";
import { useGetVoucherDistribution } from "../../api/get-voucher-distribution";
import { buildDownloadUrl } from "../api/order-confirmation.api";
import { useOrderConfirmationStatus } from "../api/use-order-confirmation-status";
import { DownloadCta } from "../components/download-cta";
import { NotFoundLink } from "../components/not-found-link";
import { TechnicalErrorLink } from "../components/technical-error-link";
import { URL_RE } from "../constants";

const extractParams = (pathname: string): { slug: string; token: string } | null => {
  const match = pathname.match(URL_RE);
  if (!match) return null;
  const [, slug, token] = match;
  return { slug, token };
};

const CenteredLoader = () => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-grow flex-col items-center justify-center px-4 py-20">
      <Loader className="size-12 text-primary" />
      <p className="mt-6 text-gray-600">{t("VOUCHERS_DISTRIBUTION.ORDER_CONFIRMATION.LOADING_DESCRIPTION")}</p>
    </div>
  );
};

const OrderConfirmationContent = ({ slug, token }: { slug: string; token: string }) => {
  const { status } = useOrderConfirmationStatus({ slug, token, enabled: true });

  if (status === "loading") return <CenteredLoader />;
  if (status === "ready") return <DownloadCta href={buildDownloadUrl({ slug, token })} />;
  if (status === "technical_error") return <TechnicalErrorLink />;
  return <NotFoundLink />;
};

type LayoutProps = {
  contractorLogoUrl?: string;
  children: React.ReactNode;
};

const Layout = ({ contractorLogoUrl, children }: LayoutProps) => (
  <div className="flex min-h-screen flex-col bg-primary-foreground">
    <Header contractorLogoUrl={contractorLogoUrl} />
    <div className="flex flex-grow flex-col">{children}</div>
    <Footer />
  </div>
);

export const OrderConfirmationPage = () => {
  const params = extractParams(window.location.pathname);

  const { data } = useGetVoucherDistribution({
    slug: params?.slug ?? "",
    config: {
      enabled: Boolean(params?.slug),
      staleTime: Number.POSITIVE_INFINITY,
      useErrorBoundary: false,
    },
  });

  if (!params) {
    return (
      <Layout>
        <NotFoundLink />
      </Layout>
    );
  }

  return (
    <Layout contractorLogoUrl={data?.voucherDistribution.contractorLogoUrl}>
      <OrderConfirmationContent slug={params.slug} token={params.token} />
    </Layout>
  );
};
